import React, { useEffect, useState } from "react";
import api from "../../lib/api";

/**
 * StatesGrid — 50 US states rendered as a status-aware grid tile tracker.
 *
 * - Not a geographically accurate map on purpose: a tile grid is mobile-friendly,
 *   AA-contrast friendly, and reads clearly at a glance. A geo-accurate SVG
 *   can be swapped in later without changing this component's API.
 * - Pulls from GET /api/journey/states and renders tiles tinted by status.
 */
export const StatesGrid = ({ onStateClick }) => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/journey/states")
      .then((r) => setStates(r.data || []))
      .catch(() => setStates([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-2 w-full"
        data-testid="states-grid-loading"
      >
        {Array.from({ length: 50 }).map((_, i) => (
          <div key={i} className="state-tile" />
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-5 sm:grid-cols-8 lg:grid-cols-10 gap-2 w-full"
      data-testid="states-grid"
    >
      {states.map((s) => (
        <button
          key={s.code}
          type="button"
          className="state-tile"
          data-status={s.status}
          data-testid={`state-tile-${s.code}`}
          onClick={() => s.status === "completed" && onStateClick?.(s)}
          disabled={s.status !== "completed"}
          title={`${s.name} — ${s.status.toUpperCase()}`}
        >
          <span className="code">{s.code}</span>
          <span className="status-dot" />
        </button>
      ))}
    </div>
  );
};

/**
 * StatesLegend — compact legend for the grid.
 */
export const StatesLegend = () => (
  <div
    className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] font-mono-tactical uppercase tracking-widest text-zinc-400"
    data-testid="states-legend"
  >
    <span className="inline-flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-zinc-500" /> Unvisited
    </span>
    <span className="inline-flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-[#FFC107]" /> Planned
    </span>
    <span className="inline-flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-[#FF1E1E] animate-pulse" /> Current
    </span>
    <span className="inline-flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-[#22c55e]" /> Completed
    </span>
  </div>
);
